/**
 * UK postcode -> address lookup.
 *
 * Calls our own backend (/api/address/lookup), which holds the provider key
 * server-side and returns the real list of houses/flats at a postcode when the
 * key is configured, or just town/region/country on the free tier. The key is
 * never shipped in the browser bundle.
 */
import { api } from '@/lib/api';

export interface AddressSuggestion {
  /** What the picker shows, e.g. "Flat 2, 14 Elm Road". */
  label: string;
  line1: string;
  line2?: string;
  city: string;
}

export interface PostcodeResult {
  city?: string;
  region?: string;
  country: string;
  postcode: string;
  /** Individual addresses at this postcode. Empty on the free tier. */
  suggestions: AddressSuggestion[];
  /** True when we could only resolve the town, not the street. */
  areaOnly: boolean;
}

const normalise = (raw: string) => (raw || '').trim().toUpperCase().replace(/\s+/g, ' ');

export async function lookupPostcode(raw: string): Promise<PostcodeResult | null> {
  const pc = normalise(raw);
  if (pc.replace(/\s/g, '').length < 5) return null;
  try {
    const { data } = await api.get('/address/lookup', { params: { postcode: pc } });
    if (!data) return null;
    return {
      city: data.city,
      region: data.region,
      country: data.country || 'United Kingdom',
      postcode: data.postcode || pc,
      suggestions: Array.isArray(data.suggestions) ? data.suggestions : [],
      areaOnly: !!data.areaOnly,
    };
  } catch {
    return null;
  }
}
